import React from "react";
import {
  Card,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useSelector } from "react-redux";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import LK_DeleteIcon from "../../../../../../images/LK_Delete.svg";
import LK_ModifyEditIcon from "../../../../../../images/LK_Modify-Edit.svg";
// Modular helpers
import { isChartType, isGaugeType, isTableType } from "./widgetTypes";
import { getChartOptions } from "./chartOptionsFactory";
import {
  buildWidgetCardSx,
  widgetHeaderSx,
  widgetTitleSx,
  widgetContentSx,
  iconBtnSx,
  WIDGET_HEADER_HEIGHT,
  resolveWidgetBackground,
} from "./widgetStyleTokens";
import EChart from "../../../EChart";
import LK_Clone from "../../../../../../images/LK_Clone.svg";

// Deprecated local constant replaced by shared token
const HEADER_HEIGHT = WIDGET_HEADER_HEIGHT;

export default function WidgetCard({
  widget,
  onEdit,
  onDelete,
  onClone,
  showPreviewGraph,
  data,
  readOnly = false,
  viewOnly = false,
}) {
  const [expandedModalOpen, setExpandedModalOpen] = React.useState(false);
  const themeMode = useSelector((state) => state?.themedata?.theme) || "light";
  const isDarkTheme = themeMode === "dark";
  const palette = {
    surface: isDarkTheme ? "#1E1E1E" : "#FFFFFF",
    surfaceAlt: isDarkTheme ? "#2C2C2C" : "#F8FAFB",
    border: isDarkTheme ? "#495057" : "#D8DFE3",
    subtleBorder: isDarkTheme ? "#3B4248" : "#E4E9EC",
    textPrimary: isDarkTheme ? "#FFFFFF" : "#1F2A33",
    textSecondary: isDarkTheme ? "#C7CED3" : "#56636E",
    headerBg: isDarkTheme ? "#2A2F35" : "#F3F5F7",
    rowHover: isDarkTheme ? "#343A40" : "#FAFBFC",
  };
  const actionIconBtnSx = {
    ...iconBtnSx,
    border: "0px",
    // bgcolor: isDarkTheme ? "rgba(44,44,44,0.9)" : "rgba(255,255,255,0.9)",
    // border: `1px solid ${isDarkTheme ? palette.subtleBorder : "#D5DCDD"}`,
    // "&:hover": {
    //   bgcolor: isDarkTheme ? "#343A40" : "#FFFFFF",
    // },
    // boxShadow: isDarkTheme
    //   ? "0 1px 2px rgba(0,0,0,0.35)"
    //   : "0 1px 2px rgba(0,0,0,0.08)",
  };
  const viewOnlyIconBtnSx = {
    padding: "4px",
    color: palette.textSecondary,
    "&:hover": {
      backgroundColor: isDarkTheme ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
    },
  };
  const rawBasic = widget?.props?.basic || {};
  const extendedProps = widget?.props?.extended || {};
  const showAlias = Boolean(extendedProps?.showAlias);

  const resolveDisplayName = React.useCallback(
    (item, fallback = "Unknown") => {
      if (!item) return fallback;
      if (showAlias) {
        return (
          item.alias ||
          item.displayname ||
          item.legendname ||
          item.datapointName ||
          item.tagname ||
          item.name ||
          item.title ||
          fallback
        );
      }
      return (
        item.datapointName ||
        item.displayname ||
        item.legendname ||
        item.tagname ||
        item.name ||
        item.title ||
        item.alias ||
        fallback
      );
    },
    [showAlias]
  );
  
  // Resolve background: user custom colour wins; otherwise follow current theme surface
  const resolvedCardBg = resolveWidgetBackground(rawBasic, themeMode);

  // Normalize basic props from backend shape
  const basicProps = {
    widgetName: widget?.widgetname || widget?.widgetName || rawBasic.widgetName,
    backgroundColor: resolvedCardBg,
    textColor:
      rawBasic.font_data?.color ||
      rawBasic.textColor ||
      (isDarkTheme ? "#FFFFFF" : "var(--LK_BrandBlue_1)"),
    fontSize:
      parseInt(
        (rawBasic.font_data?.fontsize || rawBasic.fontSize || "16")
          .toString()
          .replace("px", "")
      ) || 16,
    fontType: rawBasic.font_data?.fonttype || rawBasic.fontType || "Roboto",
    // Only apply radius if explicitly provided (avoid implicit default). If missing -> undefined so builder uses 0.
    borderRadius: (() => {
      const source = rawBasic.border?.borderradius ?? rawBasic.borderRadius;
      if (source === undefined || source === null || source === "")
        return undefined;
      const val = parseInt(source.toString().replace("px", ""));
      return isNaN(val) ? undefined : val;
    })(),
    borderSize:
      parseInt(
        (rawBasic.border?.bordersize || rawBasic.borderSize || "1")
          .toString()
          .replace("px", "")
      ) || 1,
    borderColor: rawBasic.border?.color || rawBasic.borderColor || palette.border,
    keyFontSize:
      parseInt(
        (rawBasic.keyFontSize || rawBasic.fontSize || "13")
          .toString()
          .replace("px", "")
      ) || 13,
    valueFontSize: parseInt(rawBasic.valueFontSize) || 40,
  };
  const isChart = isChartType(widget?.type);
  const isGauge = isGaugeType(widget?.type);
  const isTable = isTableType(widget?.type);
  const isStatusCard = widget?.type === "Status Card";
  const isValueCard = widget?.type === "Value Card";
  const isSchedulerStatus = widget?.type === "Scheduler Status";
  const isTagValueCard = widget?.type === "Tag Value Card";
  const isPieChart = widget?.type === "Pie Chart";
  const isDoughnutChart = widget?.type === "Doughnut Chart";
  const isDeviceTable = widget?.type === "DeviceTable";
  const isTableGateway = widget?.type === "TableGateway";
  const shouldShowEChart = (isChart || isGauge) && !isDeviceTable && !isTableGateway;
  const isLineChart = widget?.type === "Line Chart";
  const isAreaChart = widget?.type === "Area Chart";
  const isBarChart = widget?.type === "Bar Chart";
  const chartViewStyle = isLineChart || isAreaChart
    ? { width: "98%", height: "98%", borderRadius: 4 }
    : { width: "100%", height: "100%", borderRadius: 4 };

  const normalizedSeriesData = React.useMemo(() => {
    const datapointConfig = Array.isArray(extendedProps?.datapoints)
      ? extendedProps.datapoints
      : [];
    const tagConfig = Array.isArray(extendedProps?.tagprops)
      ? extendedProps.tagprops
      : [];

    const findMatchedConfig = (tagId, seriesName) => {
      const byTagId = (entry) => {
        const cfgTagId = entry?.tagid || entry?.key || entry?.id;
        return cfgTagId && String(cfgTagId) === String(tagId);
      };
      const byName = (entry) =>
        String(
          entry?.datapointName ||
            entry?.tagname ||
            entry?.displayname ||
            entry?.legendname ||
            entry?.name ||
            entry?.title ||
            entry?.alias ||
            ""
        ) === String(seriesName || "");

      return (
        datapointConfig.find(byTagId) ||
        tagConfig.find(byTagId) ||
        datapointConfig.find(byName) ||
        tagConfig.find(byName) ||
        null
      );
    };

    if (Array.isArray(data)) {
      return data.map((series) => {
        const tagId = series?.tagid || series?.id;
        const matchedConfig = findMatchedConfig(tagId, series?.name);
        return {
          ...series,
          name: resolveDisplayName(matchedConfig || series, String(tagId || series?.name || "Unknown")),
        };
      });
    }

    if (data && typeof data === "object") {
      return Object.entries(data).map(([tagid, points]) => {
        const matchedTag = findMatchedConfig(tagid);
        return {
          tagid,
          id: tagid,
          name: resolveDisplayName(matchedTag, String(tagid)),
          points: Array.isArray(points) ? points : [],
        };
      });
    }
    return [];
  }, [data, extendedProps?.datapoints, extendedProps?.tagprops, resolveDisplayName]);

  // Build index of live/historic series from data prop: data is array of series { tagid,name,points }
  const seriesIndex = React.useMemo(() => {
    const idx = {};
    if (Array.isArray(normalizedSeriesData)) {
      normalizedSeriesData.forEach((s) => {
        const k1 = s.tagid || s.id;
        if (k1) idx[k1] = s.points || [];
        const k2 = s.name;
        if (k2 && !idx[k2]) idx[k2] = s.points || [];
      });
    }
    return idx;
  }, [normalizedSeriesData]);

  const seriesMetaIndex = React.useMemo(() => {
    const idx = {};
    if (Array.isArray(normalizedSeriesData)) {
      normalizedSeriesData.forEach((series) => {
        const meta = {
          fetchSource: series.fetchSource,
          operation: series.operation,
          tagid: series.tagid || series.id,
          name: series.name,
        };
        const k1 = series.tagid || series.id;
        if (k1) idx[k1] = meta;
        const k2 = series.name;
        if (k2 && !idx[k2]) idx[k2] = meta;
      });
    }
    return idx;
  }, [normalizedSeriesData]);

  // Helper to compute average value from points array
  const avgValue = (pts) => {
    if (!Array.isArray(pts) || !pts.length) return null;
    if (pts.length === 1) {
      const v = pts[0].y ?? pts[0].value ?? pts[0].val;
      return typeof v === "number" && isFinite(v) ? v : null;
    }
    let s = 0,
      c = 0;
    pts.forEach((p) => {
      const v = p.y ?? p.value ?? p.val;
      if (typeof v === "number" && isFinite(v)) {
        s += v;
        c++;
      }
    });
    return c ? s / c : null;
  };

  // Derive list items: prefer internal datapoints, else backend tagprops, attaching points/value
  const tagItems = (extendedProps?.tagprops || []).map((t, idx) => {
    const key = t.tagid || idx;
    const name = resolveDisplayName(t, String(key));
    const points = seriesIndex[key] || seriesIndex[name] || [];
    const seriesMeta = seriesMetaIndex[key] || seriesMetaIndex[name] || {};
    return {
      key,
      name,
      unit: t.unit,
      color: t.chartprops?.color || basicProps.textColor,
      backgroundColor: t.backgroundColor || t.cardBackground || "#FFFFFF",
      switchPosition: t.switch_position,
      gaugeLabel: t.gauge_label,
      type: t.charttype,
      raw: t,
      points,
      value: avgValue(points),
      configuredSource:
        String(t.dataType || extendedProps?.dataType || "live").toLowerCase() === "historic"
          ? "historic"
          : "live",
      fetchedSource: seriesMeta.fetchSource || null,
    };
  });
  const datapointItems = (extendedProps?.datapoints || []).map((d, idx) => {
    const key = d.id || d.key || idx;
    const name = resolveDisplayName(d, String(key));
    const points = seriesIndex[key] || seriesIndex[name] || [];
    const seriesMeta = seriesMetaIndex[key] || seriesMetaIndex[name] || {};
    return {
      key,
      name,
      color: d.textColor || basicProps.textColor,
      backgroundColor: d.backgroundColor || d.cardBackground || "#FFFFFF",
      unit: d.storageSelection || d.unit,
      raw: d,
      points,
      value: avgValue(points),
      configuredSource:
        String(d.dataType || extendedProps?.dataType || "live").toLowerCase() === "historic"
          ? "historic"
          : "live",
      fetchedSource: seriesMeta.fetchSource || null,
    };
  });
  const displayItems = datapointItems.length ? datapointItems : tagItems;

  const statusCardItems = React.useMemo(() => {
    const source = Array.isArray(extendedProps?.statusItems)
      ? extendedProps.statusItems
      : displayItems;
    return source.map((item, idx) => {
      const points = Array.isArray(item.points) ? item.points : [];
      const latestPoint = [...points]
        .reverse()
        .find((p) => p && (p.y ?? p.value ?? p.val) !== undefined);
      const latestValue =
        latestPoint?.y ?? latestPoint?.value ?? latestPoint?.val ?? item.value;
      const tsRaw = latestPoint?.x ?? latestPoint?.timestamp ?? latestPoint?.time;
      const tsNum = Number(tsRaw);
      const tsMs =
        tsNum && !Number.isNaN(tsNum)
          ? tsNum * (String(tsNum).length < 13 ? 1000 : 1)
          : null;
      return {
        ...item,
        key: item.key || idx,
        deviceName:
          item.deviceName || item.name || item.title || `Device ${idx + 1}`,
        parameter:
          (showAlias
            ? (item.alias || item.raw?.alias || item.parameter || item.raw?.parameter || item.name)
            : (item.parameter || item.raw?.parameter || item.alias || item.raw?.alias || item.name)) ||
          "—",
        value: latestValue,
        unit: item.unit,
        timestamp: tsMs ? new Date(tsMs).toISOString() : item.timestamp,
        status: item.status || item.raw?.status || item.raw?.state,
        configuredSource:
          item.configuredSource ||
          (String(item.raw?.dataType || extendedProps?.dataType || "live").toLowerCase() === "historic"
            ? "historic"
            : "live"),
        fetchedSource: item.fetchedSource || item.raw?.fetchedSource || null,
        communicationStatus:
          item.communicationStatus ||
          item.raw?.communicationStatus ||
          item.raw?.commStatus,
      };
    });
  }, [displayItems, extendedProps?.statusItems, showAlias]);

  const shouldShowLiveTimestamp = React.useMemo(() => {
    const globalMode = String(extendedProps?.dataType || "").toLowerCase();
    if (globalMode === "live") return true;

    const datapoints = Array.isArray(extendedProps?.datapoints)
      ? extendedProps.datapoints
      : [];
    if (
      datapoints.some(
        (dp) => String(dp?.dataType || "").toLowerCase() === "live"
      )
    ) {
      return true;
    }

    const tagprops = Array.isArray(extendedProps?.tagprops)
      ? extendedProps.tagprops
      : [];
    return tagprops.some(
      (tag) => String(tag?.dataType || "").toLowerCase() === "live"
    );
  }, [extendedProps?.dataType, extendedProps?.datapoints, extendedProps?.tagprops]);

  const latestSyncTimestamp = React.useMemo(() => {
    const toMillis = (rawTs) => {
      if (rawTs === undefined || rawTs === null || rawTs === "") return null;
      const numericTs = Number(rawTs);
      if (!Number.isNaN(numericTs) && numericTs > 0) {
        return numericTs * (String(Math.trunc(numericTs)).length < 13 ? 1000 : 1);
      }
      const parsed = new Date(rawTs).getTime();
      return Number.isNaN(parsed) ? null : parsed;
    };

    let latestMs = null;

    if (Array.isArray(normalizedSeriesData)) {
      normalizedSeriesData.forEach((series) => {
        const points = Array.isArray(series?.points) ? series.points : [];
        points.forEach((point) => {
          const pointTs = point?.x ?? point?.timestamp ?? point?.time;
          const ms = toMillis(pointTs);
          if (ms && (!latestMs || ms > latestMs)) latestMs = ms;
        });
      });
    }

    if (!latestMs && Array.isArray(statusCardItems)) {
      statusCardItems.forEach((item) => {
        const ms = toMillis(item?.timestamp);
        if (ms && (!latestMs || ms > latestMs)) latestMs = ms;
      });
    }

    return latestMs ? new Date(latestMs).toISOString() : null;
  }, [normalizedSeriesData, statusCardItems]);

  // In live mode some widgets may receive values without timestamp metadata.
  // Fall back to current time so "Last sync time" is still visible in the UI.
  const effectiveSyncTimestamp = React.useMemo(() => {
    if (!shouldShowLiveTimestamp) return null;
    return latestSyncTimestamp || new Date().toISOString();
  }, [shouldShowLiveTimestamp, latestSyncTimestamp]);

  // Specialized render for switch_gauge or similar types: show gauge vs target vs total groups
  const isSwitchGauge = widget?.type === "switch_gauge";
  let grouped = {};
  if (isSwitchGauge) {
    grouped = displayItems.reduce((acc, it) => {
      const label = it.gaugeLabel || it.raw?.gauge_label || "value";
      acc[label] = acc[label] || [];
      acc[label].push(it);
      return acc;
    }, {});
  }

  return (
    <Card
      elevation={0}
      sx={buildWidgetCardSx(basicProps, {
        width: "100%",
        height: "100%",
      })}
    >

      <Box sx={widgetHeaderSx(basicProps)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
            backgroundColor: resolvedCardBg,
          }}
        >
          <Typography
            component="h6"
            sx={widgetTitleSx(basicProps)}
            title={basicProps.widgetName || "Widget"}
          >
            {basicProps.widgetName || "Widget"}
          </Typography>
          {/* required for future functionality */}
          {viewOnly && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {/* <Tooltip title="Expand View" arrow> //commented for future use in case of need
                <IconButton
                  size="small"
                  // onClick={() => setExpandedModalOpen(true)}
                  sx={viewOnlyIconBtnSx}
                >
                  <ZoomOutMapIcon
                    sx={{
                      width: "16px",
                      height: "16px",
                      color: palette.textSecondary,
                    }}
                  />
                </IconButton>
              </Tooltip> */}
              {/* <Tooltip title="Widget Info" arrow>
                <IconButton
                  size="small"
                  sx={viewOnlyIconBtnSx}
                >
                  <InfoOutlinedIcon
                    sx={{
                      width: "18px",
                      height: "18px",
                      color: palette.textSecondary,
                    }}
                  />
                </IconButton>
              </Tooltip> */}
            </div>
          )}
          {!viewOnly && readOnly && (
            <span
              className="widget-drag-handle"
              style={{
                cursor: "move",
                width: 12,
                height: 12,
                display: "inline-block",
                backgroundColor: palette.textSecondary,
              }}
            />
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        {!readOnly && (
          <Box sx={{ display: "inline-flex", gap: 0.5 }}>
            <Tooltip title="Edit" arrow>
              <IconButton
                size="small"
                aria-label="edit"
                onClick={() => onEdit && onEdit(widget)}
                sx={actionIconBtnSx}
              >
                <img
                  src={LK_ModifyEditIcon}
                  width={18}
                  height={18}
                  alt="edit"
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <IconButton
                size="small"
                aria-label="delete"
                onClick={() => onDelete && onDelete(widget)}
                sx={actionIconBtnSx}
              >
                <img src={LK_DeleteIcon} width={18} height={18} alt="delete" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clone" arrow>
              <IconButton
                size="small"
                aria-label="clone"
                onClick={() => onClone && onClone(widget)}
                sx={actionIconBtnSx}
              >
                <img src={LK_Clone} width={18} height={18} alt="clone" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      {/* <Divider sx={{ borderColor: 'var(--CardDivider,#E5E9E8)' }} /> */}
      {/* <Divider sx={{ borderColor: 'var(--CardDivider,#E5E9E8)' }} /> */}
      <Box
        sx={{
          ...widgetContentSx,
          position: "relative",
          pb: effectiveSyncTimestamp ? 3.5 : widgetContentSx?.pb,
        }}
      >
        {isValueCard ? (
          <ValueCardContent
            displayItems={displayItems}
            basicProps={basicProps}
            extendedProps={extendedProps}
            themeMode={themeMode}
          />
        ) : isTagValueCard ? (
          <TagValueCardContent
            displayItems={displayItems}
            basicProps={basicProps}
            extendedProps={extendedProps}
            themeMode={themeMode}
          />
        ) : isSchedulerStatus ? (
          <SchedulerStatusCardContent
            displayItems={displayItems}
            basicProps={basicProps}
            extendedProps={extendedProps}
            themeMode={themeMode}
          />
        ) : isStatusCard ? (
          <StatusCardGrid
            items={statusCardItems}
            textColor={basicProps.textColor}
            fontType={basicProps.fontType}
            themeMode={themeMode}
            extendedProps={extendedProps}
          />
        ) : shouldShowEChart && showPreviewGraph ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
                        }}
          >
            <EChart
              chartType={widget.type}
              options={getChartOptions(
                widget.type,
                basicProps,
                extendedProps,
                (() => {
                  if (!Array.isArray(normalizedSeriesData)) return [];
                  
                  // For Pie/Doughnut charts: calculate average per series/tag
                  if (isPieChart || isDoughnutChart) {
                    return normalizedSeriesData.map((series) => {
                      const points = Array.isArray(series?.points) ? series.points : [];
                      // Calculate average of y values
                      let sum = 0;
                      let count = 0;
                      points.forEach((p) => {
                        const val = p.y ?? p.value ?? p.val;
                        if (typeof val === "number" && isFinite(val)) {
                          sum += val;
                          count++;
                        }
                      });
                      const avgValue = count > 0 ? sum / count : 0;
                      
                      return {
                        name: series.name || series.tagname || series.tagid || "Unknown",
                        value: avgValue,
                        datapointName: series.name || series.tagname
                      };
                    }).filter((item) => item.value !== 0 || normalizedSeriesData.length === 0);
                  }

                  // For line/area/bar charts keep grouped series so each tag plots independently
                  if (isLineChart || isAreaChart || isBarChart) {
                    const mergedByMetric = normalizedSeriesData.reduce((acc, series, idx) => {
                      const metricKey = String(
                        series?.tagid ||
                          series?.id ||
                          series?.name ||
                          series?.tagname ||
                          `series-${idx}`
                      );
                      const normalizedPoints = Array.isArray(series?.points)
                        ? series.points
                            .filter(
                              (p) =>
                                p &&
                                (p.x !== undefined || p.timestamp || p.time) &&
                                (p.y !== undefined || p.value !== undefined)
                            )
                            .map((p) => ({
                              x: p.x ?? p.timestamp ?? p.time ?? Date.now(),
                              y:
                                typeof (p.y ?? p.value) === "number"
                                  ? p.y ?? p.value
                                  : Number(p.y ?? p.value),
                            }))
                            .filter((p) => !isNaN(p.x) && !isNaN(p.y))
                        : [];

                      const current =
                        acc.get(metricKey) ||
                        {
                          tagid: series?.tagid || series?.id,
                          name:
                            series?.name ||
                            series?.tagname ||
                            series?.tagid ||
                            "Unknown",
                          color: series?.color,
                          points: [],
                        };

                      if (!current.color && series?.color) {
                        current.color = series.color;
                      }

                      if (normalizedPoints.length > 0) {
                        const pointsByX = new Map(
                          current.points.map((point) => [String(point.x), point])
                        );
                        normalizedPoints.forEach((point) => {
                          pointsByX.set(String(point.x), point);
                        });
                        current.points = Array.from(pointsByX.values()).sort(
                          (a, b) => Number(a.x) - Number(b.x)
                        );
                      }

                      acc.set(metricKey, current);
                      return acc;
                    }, new Map());

                    return Array.from(mergedByMetric.values()).filter((s) =>
                      isBarChart
                        ? !!s?.name
                        : Array.isArray(s.points) && s.points.length > 0
                    );
                  }
                  
                  // Accept series[] where each has points or a flat array of points
                  const seriesPoints = normalizedSeriesData.flatMap((s) =>
                    Array.isArray(s?.points) ? s.points : []
                  );
                  // For tables and gauges, preserve all original properties
                  if (isTable || isGauge) {
                    return seriesPoints.filter(
                      (p) => p && (p.y !== undefined || p.value !== undefined)
                    );
                  }
                  // For charts, sanitize to x & y format
                  return seriesPoints
                    .filter(
                      (p) =>
                        p &&
                        (p.x !== undefined || p.timestamp || p.time) &&
                        p.y !== undefined &&
                        p.y !== null
                    )
                    .map((p) => ({
                      x: p.x ?? p.timestamp ?? p.time ?? Date.now(),
                      y: typeof p.y === "number" ? p.y : Number(p.y),
                    }))
                    .filter((p) => !isNaN(p.x) && !isNaN(p.y));
                })()
              )}
              style={chartViewStyle}
            />
          </Box>
        ) : isTable && showPreviewGraph ? (
          <TableContainer component={Paper} elevation={0} sx={{ boxShadow: 'none', 
          width: '100%',
           backgroundColor: palette.surface }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 700,
                      fontStyle: 'normal',
                      fontSize: 12,
                      lineHeight: '100%',
                      letterSpacing: 0,
                      color: palette.textSecondary,
                      backgroundColor: palette.headerBg,
                    }}
                  >
                    Tag Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 700,
                      fontStyle: 'normal',
                      fontSize: 12,
                      lineHeight: '100%',
                      letterSpacing: 0,
                      color: palette.textSecondary,
                      backgroundColor: palette.headerBg,
                    }}
                    align="right"
                  >
                    Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(displayItems.length ? displayItems : [{ key: 'none', name: 'No Data', value: '—' }]).map((item) => {
                  const points = Array.isArray(item.points) ? item.points : [];
                  const latest = (() => {
                    const last = [...points].reverse().find(p => p && (p.y ?? p.value ?? p.val) !== undefined);
                    if (!last) return item.value;
                    const raw = last.y ?? last.value ?? last.val;
                    return typeof raw === 'number' && isFinite(raw) ? raw : Number(raw);
                  })();
                  const formatted = (() => {
                    if (latest === null || latest === undefined || Number.isNaN(latest)) return '—';
                    if (typeof latest === 'number') {
                      const abs = Math.abs(latest);
                      let str = abs >= 1000
                        ? latest.toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : latest.toFixed(2);
                      str = str.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
                      return str;
                    }
                    return String(latest);
                  })();
                  const bg = item.backgroundColor || palette.surface;
                  const fg = item.textColor || basicProps.textColor || palette.textPrimary;
                  return (
                    <TableRow key={item.key} sx={{ backgroundColor: bg, '&:hover': { backgroundColor: palette.rowHover }, borderBottom: `1px solid ${palette.subtleBorder}` }}>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: fg }}>
                        {item.name || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 400, color: fg }} align="right">
                        {formatted}
                        {item.unit ? <span style={{ marginLeft: 4, color: palette.textSecondary, fontSize: 10 }}>{item.unit}</span> : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : isDeviceTable && showPreviewGraph ? (
          <TableContainer component={Paper} elevation={0} sx={{ boxShadow: 'none', width: '100%', backgroundColor: palette.surface }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: 13, color: palette.textSecondary, backgroundColor: palette.headerBg }}>
                    Device Name
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: 13, color: palette.textSecondary, backgroundColor: palette.headerBg }}>
                    Serial Number
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: 13, color: palette.textSecondary, backgroundColor: palette.headerBg }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { id: 1, name: 'Device-01', serialNumber: 'SN-12345', status: 'Connected' },
                  { id: 2, name: 'Device-02', serialNumber: 'SN-12346', status: 'Connected' },
                  { id: 3, name: 'Device-03', serialNumber: 'SN-12347', status: 'Disconnected' },
                  { id: 4, name: 'Device-04', serialNumber: 'SN-12348', status: 'Connected' },
                  { id: 5, name: 'Device-05', serialNumber: 'SN-12349', status: 'Connected' },
                  { id: 6, name: 'Device-06', serialNumber: 'SN-12350', status: 'Disconnected' },
                ].map((row) => (
                  <TableRow key={row.id} sx={{ borderBottom: `1px solid ${palette.subtleBorder}`, backgroundColor: palette.surface, '&:hover': { backgroundColor: palette.rowHover } }}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 500, color: palette.textPrimary }}>
                      {row.name}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 500, color: palette.textSecondary }}>
                      {row.serialNumber}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 500, color: row.status === 'Connected' ? '#00943D' : '#DC272D' }}>
                      {row.status}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : isTableGateway && showPreviewGraph ? (
          <TableContainer component={Paper} elevation={0} sx={{ boxShadow: 'none', width: '100%', backgroundColor: palette.surface }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: 13, color: palette.textSecondary, backgroundColor: palette.headerBg }}>
                    Gateway Name
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: 13, color: palette.textSecondary, backgroundColor: palette.headerBg }}>
                    Serial Number
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: 13, color: palette.textSecondary, backgroundColor: palette.headerBg }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { id: 1, name: 'Gateway-01', serialNumber: 'GW-12345', status: 'Connected' },
                  { id: 2, name: 'Gateway-02', serialNumber: 'GW-12346', status: 'Connected' },
                  { id: 3, name: 'Gateway-03', serialNumber: 'GW-12347', status: 'Disconnected' },
                  { id: 4, name: 'Gateway-04', serialNumber: 'GW-12348', status: 'Connected' },
                  { id: 5, name: 'Gateway-05', serialNumber: 'GW-12349', status: 'unknown' },
                  { id: 6, name: 'Gateway-06', serialNumber: 'GW-12350', status: 'Connected' },
                ].map((row) => (
                  <TableRow key={row.id} sx={{ borderBottom: `1px solid ${palette.subtleBorder}`, backgroundColor: palette.surface, '&:hover': { backgroundColor: palette.rowHover } }}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 500, color: palette.textPrimary }}>
                      {row.name}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 500, color: palette.textSecondary }}>
                      {row.serialNumber}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: 500, color: (row.status === 'Connected') ? '#00943D' : ((row.status === 'Disconnected' || row.status === 'unknown') ? '#DC272D' : palette.textSecondary) }}>
                      {row.status}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : isSwitchGauge ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              overflow: "auto",
            }}
          >
            {Object.keys(grouped).map((group) => (
              <Box
                key={group}
                sx={{
                  border: `1px solid ${palette.subtleBorder}`,
                  borderRadius: 1.5,
                  p: 1,
                  background: palette.surfaceAlt,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: palette.textSecondary,
                    letterSpacing: 0.4,
                  }}
                >
                  {group.toUpperCase()}
                </Typography>
                {grouped[group].map((item) => (
                  <LineItem
                    key={item.key}
                    item={item}
                    fontType={basicProps.fontType}
                    textColor={basicProps.textColor}
                    themeMode={themeMode}
                    palette={palette}
                  />
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              overflow: "auto",
            }}
          >
            {(displayItems.length
              ? displayItems
              : [{ key: "none", name: "No Data", color: "#C1C7CC" }]
            ).map((item) => (
              <LineItem
                key={item.key}
                item={item}
                fontType={basicProps.fontType}
                textColor={basicProps.textColor}
                themeMode={themeMode}
                palette={palette}
              />
            ))}
          </Box>
        )}

        {effectiveSyncTimestamp && (
          <Typography
            sx={{
              fontSize: 11,
              color: palette.textSecondary,
              fontFamily: basicProps.fontType || "Roboto",
              textAlign: "right",
              position: "absolute",
              right: 8,
              bottom: 6,
            }}
          >
            Last sync time: {formatTimestamp(effectiveSyncTimestamp)}
          </Typography>
        )}
      </Box>

      {/* Expanded Modal Dialog */}
      <Dialog
        open={expandedModalOpen}
        onClose={() => setExpandedModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: palette.surface,
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: palette.headerBg,
            borderBottom: `1px solid ${palette.subtleBorder}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: palette.textPrimary,
              fontFamily: basicProps.fontType || "Roboto",
            }}
          >
            {basicProps.widgetName || "Widget"} - Expanded View
          </Typography>
          <Button
            onClick={() => setExpandedModalOpen(false)}
            sx={{
              color: palette.textSecondary,
              textTransform: "none",
              fontSize: 14,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: palette.surface,
            padding: 2,
            minHeight: 500,
          }}
        >
          {/* Render the same content as the widget card but in expanded mode */}
          {isValueCard ? (
            <ValueCardContent
              displayItems={displayItems}
              basicProps={basicProps}
              extendedProps={extendedProps}
              themeMode={themeMode}
            />
          ) : isTagValueCard ? (
            <TagValueCardContent
              displayItems={displayItems}
              basicProps={basicProps}
              extendedProps={extendedProps}
              themeMode={themeMode}
            />
          ) : isSchedulerStatus ? (
            <SchedulerStatusCardContent
              displayItems={displayItems}
              basicProps={basicProps}
              extendedProps={extendedProps}
              themeMode={themeMode}
            />
          ) : isStatusCard ? (
            <StatusCardGrid
              items={statusCardItems}
              textColor={basicProps.textColor}
              fontType={basicProps.fontType}
              themeMode={themeMode}
              extendedProps={extendedProps}
            />
          ) : shouldShowEChart && showPreviewGraph ? (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                height: 500,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EChart
                chartType={widget.type}
                options={getChartOptions(
                  widget.type,
                  basicProps,
                  extendedProps,
                  (() => {
                    if (!Array.isArray(normalizedSeriesData)) return [];
                    
                    // For Pie/Doughnut charts: calculate average per series/tag
                    if (isPieChart || isDoughnutChart) {
                      return normalizedSeriesData.map((series) => {
                        const points = Array.isArray(series?.points) ? series.points : [];
                        let sum = 0;
                        let count = 0;
                        points.forEach((p) => {
                          const val = p.y ?? p.value ?? p.val;
                          if (typeof val === "number" && isFinite(val)) {
                            sum += val;
                            count++;
                          }
                        });
                        const avgValue = count > 0 ? sum / count : 0;
                        
                        return {
                          name: series.name || series.tagname || series.tagid || "Unknown",
                          value: avgValue,
                          datapointName: series.name || series.tagname
                        };
                      }).filter((item) => item.value !== 0 || normalizedSeriesData.length === 0);
                    }

                    // For line/area/bar charts keep grouped series so each tag plots independently
                    if (isLineChart || isAreaChart || isBarChart) {
                      const mergedByMetric = normalizedSeriesData.reduce((acc, series, idx) => {
                        const metricKey = String(
                          series?.tagid ||
                            series?.id ||
                            series?.name ||
                            series?.tagname ||
                            `series-${idx}`
                        );
                        const normalizedPoints = Array.isArray(series?.points)
                          ? series.points
                              .filter(
                                (p) =>
                                  p &&
                                  (p.x !== undefined || p.timestamp || p.time) &&
                                  (p.y !== undefined || p.value !== undefined)
                              )
                              .map((p) => ({
                                x: p.x ?? p.timestamp ?? p.time ?? Date.now(),
                                y:
                                  typeof (p.y ?? p.value) === "number"
                                    ? p.y ?? p.value
                                    : Number(p.y ?? p.value),
                              }))
                              .filter((p) => !isNaN(p.x) && !isNaN(p.y))
                          : [];

                        const current =
                          acc.get(metricKey) ||
                          {
                            tagid: series?.tagid || series?.id,
                            name:
                              series?.name ||
                              series?.tagname ||
                              series?.tagid ||
                              "Unknown",
                            color: series?.color,
                            points: [],
                          };

                        if (!current.color && series?.color) {
                          current.color = series.color;
                        }

                        if (normalizedPoints.length > 0) {
                          const pointsByX = new Map(
                            current.points.map((point) => [String(point.x), point])
                          );
                          normalizedPoints.forEach((point) => {
                            pointsByX.set(String(point.x), point);
                          });
                          current.points = Array.from(pointsByX.values()).sort(
                            (a, b) => Number(a.x) - Number(b.x)
                          );
                        }

                        acc.set(metricKey, current);
                        return acc;
                      }, new Map());

                      return Array.from(mergedByMetric.values()).filter((s) =>
                        isBarChart
                          ? !!s?.name
                          : Array.isArray(s.points) && s.points.length > 0
                      );
                    }
                    
                    const seriesPoints = normalizedSeriesData.flatMap((s) =>
                      Array.isArray(s?.points) ? s.points : []
                    );
                    if (isTable || isGauge) {
                      return seriesPoints.filter(
                        (p) => p && (p.y !== undefined || p.value !== undefined)
                      );
                    }
                    return seriesPoints
                      .filter(
                        (p) =>
                          p &&
                          (p.x !== undefined || p.timestamp || p.time) &&
                          p.y !== undefined &&
                          p.y !== null
                      )
                      .map((p) => ({
                        x: p.x ?? p.timestamp ?? p.time ?? Date.now(),
                        y: typeof p.y === "number" ? p.y : Number(p.y),
                      }))
                      .filter((p) => !isNaN(p.x) && !isNaN(p.y));
                  })()
                )}
                style={{ width: "100%", height: "100%", borderRadius: 4 }}
              />
            </Box>
          ) : isTable && showPreviewGraph ? (
            <TableContainer component={Paper} elevation={0} sx={{ boxShadow: 'none', width: '100%', backgroundColor: palette.surface }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 700,
                        fontStyle: 'normal',
                        fontSize: 12,
                        lineHeight: '100%',
                        letterSpacing: 0,
                        color: palette.textSecondary,
                        backgroundColor: palette.headerBg,
                      }}
                    >
                      Tag Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 700,
                        fontStyle: 'normal',
                        fontSize: 12,
                        lineHeight: '100%',
                        letterSpacing: 0,
                        color: palette.textSecondary,
                        backgroundColor: palette.headerBg,
                      }}
                      align="right"
                    >
                      Value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(displayItems.length ? displayItems : [{ key: 'none', name: 'No Data', value: '—' }]).map((item) => {
                    const points = Array.isArray(item.points) ? item.points : [];
                    const latest = (() => {
                      const last = [...points].reverse().find(p => p && (p.y ?? p.value ?? p.val) !== undefined);
                      if (!last) return item.value;
                      const raw = last.y ?? last.value ?? last.val;
                      return typeof raw === 'number' && isFinite(raw) ? raw : Number(raw);
                    })();
                    const formatted = (() => {
                      if (latest === null || latest === undefined || Number.isNaN(latest)) return '—';
                      if (typeof latest === 'number') {
                        const abs = Math.abs(latest);
                        let str = abs >= 1000
                          ? latest.toLocaleString(undefined, { maximumFractionDigits: 2 })
                          : latest.toFixed(2);
                        str = str.replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
                        return str;
                      }
                      return String(latest);
                    })();
                    const bg = item.backgroundColor || palette.surface;
                    const fg = item.textColor || basicProps.textColor || palette.textPrimary;
                    return (
                      <TableRow key={item.key} sx={{ backgroundColor: bg, '&:hover': { backgroundColor: palette.rowHover }, borderBottom: `1px solid ${palette.subtleBorder}` }}>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600, color: fg }}>
                          {item.name || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 400, color: fg }} align="right">
                          {formatted}
                          {item.unit ? <span style={{ marginLeft: 4, color: palette.textSecondary, fontSize: 10 }}>{item.unit}</span> : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                overflow: "auto",
              }}
            >
              {(displayItems.length
                ? displayItems
                : [{ key: "none", name: "No Data", color: "#C1C7CC" }]
              ).map((item) => (
                <LineItem
                  key={item.key}
                  item={item}
                  fontType={basicProps.fontType}
                  textColor={basicProps.textColor}
                  themeMode={themeMode}
                  palette={palette}
                />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Removed local getDefaultChartOptions logic; chartOptionsFactory handles chart option creation now.

// Value Card: shows a single large value centered, with optional live timestamp
function ValueCardContent({ displayItems, basicProps, extendedProps, themeMode = "light" }) {
  const isDarkTheme = themeMode === "dark";
  const secondaryText = isDarkTheme ? "#C7CED3" : "#56636E";
  const isLive = extendedProps?.dataType === "live" || extendedProps?.dataType === "mixed";
  const item = displayItems.length > 0 ? displayItems[0] : null;
  const keyFontSize = basicProps.keyFontSize || basicProps.fontSize || 13;

  // Compute the latest value and timestamp
  const rawPoints = item?.points || item?.raw?.points || [];
  const points = Array.isArray(rawPoints) ? rawPoints : [];
  const latestPoint = [...points].reverse().find(
    (p) => p && (p.y ?? p.value ?? p.val) !== undefined
  );
  let displayValue = item?.value;
  if ((displayValue === undefined || displayValue === null) && latestPoint) {
    displayValue = latestPoint.y ?? latestPoint.value ?? latestPoint.val;
  }
  if ((displayValue === undefined || displayValue === null) && points.length > 0) {
    let sum = 0, count = 0;
    points.forEach((p) => {
      const v = p.y ?? p.value ?? p.val;
      if (typeof v === "number" && isFinite(v)) { sum += v; count++; }
    });
    if (count) displayValue = sum / count;
  }

  const formatted = (() => {
    if (displayValue === null || displayValue === undefined) return "—";
    if (typeof displayValue === "number") {
      const abs = Math.abs(displayValue);
      let str = abs >= 1000
        ? displayValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : displayValue.toFixed(2);
      str = str.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
      return str;
    }
    return String(displayValue);
  })();

  // Timestamp from latest point
  const tsRaw = latestPoint?.x ?? latestPoint?.timestamp ?? latestPoint?.time;
  const tsNum = Number(tsRaw);
  const tsMs = tsNum && !Number.isNaN(tsNum)
    ? tsNum * (String(tsNum).length < 13 ? 1000 : 1)
    : null;
  const timestampStr = tsMs ? new Date(tsMs).toLocaleString() : "";

  const valueFontSize = basicProps.valueFontSize || 40;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        width: "100%",
        height: "100%",
        gap: 0.5,
      }}
    >
      <Typography
        sx={{
          fontSize: valueFontSize,
          fontWeight: 400,
          fontFamily: basicProps.fontType || "Roboto",
          color: item?.color || basicProps.textColor,
          lineHeight: 1.2,
          textAlign: "center",
        }}
      >
        {formatted}
      </Typography>
      {isLive && timestampStr && (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            fontFamily: basicProps.fontType || "Roboto",
            color: secondaryText,
            textAlign: "center",
          }}
        >
          {timestampStr}
        </Typography>
      )}
      {/* Tag name + unit at the bottom */}
      {item && (
        <Typography
          sx={{
            fontSize: keyFontSize,
            fontWeight: 400,
            fontFamily: basicProps.fontType || "Roboto",
            color: secondaryText,
            textAlign: "center",
            pt: 0.5,
          }}
        >
          {item.name || "—"}
          {(item.unit || item.raw?.Unit || item.raw?.storageSelection) ? (
            <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 400 }}>
              ({item.unit || item.raw?.Unit || item.raw?.storageSelection})
            </span>
          ) : null}
        </Typography>
      )}
    </Box>
  );
}

// Tag Value Card: shows up to 2 datapoints each with TagName (unit) label above the value, live timestamp at bottom
function TagValueCardContent({ displayItems, basicProps, extendedProps, themeMode = "light" }) {
  const isDarkTheme = themeMode === "dark";
  const secondaryText = isDarkTheme ? "#C7CED3" : "#56636E";
  const isLive = extendedProps?.dataType === "live" || extendedProps?.dataType === "mixed";
  const keyFontSize = basicProps.keyFontSize || basicProps.fontSize || 13;

  const resolveLatest = (item) => {
    const rawPoints = item?.points || item?.raw?.points || [];
    const points = Array.isArray(rawPoints) ? rawPoints : [];
    const latestPoint = [...points].reverse().find(
      (p) => p && (p.y ?? p.value ?? p.val) !== undefined
    );
    let displayValue = item?.value;
    if ((displayValue === undefined || displayValue === null) && latestPoint) {
      displayValue = latestPoint.y ?? latestPoint.value ?? latestPoint.val;
    }
    const tsRaw = latestPoint?.x ?? latestPoint?.timestamp ?? latestPoint?.time;
    const tsNum = Number(tsRaw);
    const tsMs = tsNum && !Number.isNaN(tsNum)
      ? tsNum * (String(tsNum).length < 13 ? 1000 : 1)
      : null;
    return {
      value: displayValue,
      timestamp: tsMs ? new Date(tsMs).getTime() : null,
    };
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "\u2014";
    if (typeof value === "number") {
      const abs = Math.abs(value);
      let str = abs >= 1000
        ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : value.toFixed(2);
      str = str.replace(/\.0+$/, "").replace(/(\.[\d]*[1-9])0+$/, "$1");
      return str;
    }
    return String(value);
  };

  const items = displayItems.slice(0, 2);
  const rowValueSize = Math.max(16, Math.round((basicProps.valueFontSize || 40) * 0.45));

  const latestTimestamp = React.useMemo(() => {
    const times = items.map((it) => resolveLatest(it).timestamp).filter((v) => typeof v === "number");
    return times.length ? Math.max(...times) : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayItems]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        flex: 1,
        width: "100%",
        height: "100%",
        gap: 1,
        px: 1,
      }}
    >
      <Box sx={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: items.length > 1 ? "1fr 1fr" : "1fr",
            columnGap: 2,
            alignItems: "end",
            width: "min(100%, 460px)",
            mx: "auto",
          }}
        >
          {items.map((item, idx) => {
            const unit = item?.unit || item?.raw?.Unit || item?.raw?.storageSelection;
            return (
              <Typography
                key={idx}
                sx={{
                  fontSize: keyFontSize,
                  fontWeight: 400,
                  fontFamily: basicProps.fontType || "Roboto",
                  color: secondaryText,
                  textAlign: "center",
                }}
              >
                {item?.name || item?.tagname || "\u2014"}
                {unit ? (
                  <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 400 }}>({unit})</span>
                ) : null}
              </Typography>
            );
          })}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: items.length > 1 ? "1fr 1fr" : "1fr",
            columnGap: 2,
            alignItems: "baseline",
            width: "min(100%, 460px)",
            mx: "auto",
          }}
        >
          {items.map((item, idx) => {
            const resolved = resolveLatest(item);
            return (
              <Typography
                key={idx}
                sx={{
                  fontSize: rowValueSize,
                  fontWeight: 400,
                  fontFamily: basicProps.fontType || "Roboto",
                  color: item?.color || basicProps.textColor,
                  lineHeight: 1.2,
                  textAlign: "center",
                }}
              >
                {formatValue(resolved.value)}
              </Typography>
            );
          })}
        </Box>
      </Box>
      {/* {isLive && latestTimestamp ? ( //commented for timestamp
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            fontFamily: basicProps.fontType || "Roboto",
            color: secondaryText,
            textAlign: "center",
            position: "absolute",
            right: 8,
            bottom: 6,
            padding: "10px",
          }}
        >
          {new Date(latestTimestamp).toLocaleString()}
        </Typography>
      ) : null} */}
    </Box>
  );
}

// Scheduler Status card: shows Start Time and Stop Time tags similar to Value Card styling
function SchedulerStatusCardContent({ displayItems, basicProps, extendedProps, themeMode = "light" }) {
  const isDarkTheme = themeMode === "dark";
  const secondaryText = isDarkTheme ? "#C7CED3" : "#56636E";
  const isLive = extendedProps?.dataType === "live" || extendedProps?.dataType === "mixed";
  const startItem = displayItems.length > 0 ? displayItems[0] : null;
  const stopItem = displayItems.length > 1 ? displayItems[1] : null;
  const keyFontSize = basicProps.keyFontSize || basicProps.fontSize || 13;

  const resolveLatest = (item) => {
    const rawPoints = item?.points || item?.raw?.points || [];
    const points = Array.isArray(rawPoints) ? rawPoints : [];
    const latestPoint = [...points].reverse().find(
      (p) => p && (p.y ?? p.value ?? p.val) !== undefined
    );

    let displayValue = item?.value;
    if ((displayValue === undefined || displayValue === null) && latestPoint) {
      displayValue = latestPoint.y ?? latestPoint.value ?? latestPoint.val;
    }
    const tsRaw = latestPoint?.x ?? latestPoint?.timestamp ?? latestPoint?.time;
    const tsNum = Number(tsRaw);
    const tsMs = tsNum && !Number.isNaN(tsNum)
      ? tsNum * (String(tsNum).length < 13 ? 1000 : 1)
      : null;
    return {
      value: displayValue,
      timestamp: tsMs ? new Date(tsMs).getTime() : null,
    };
  };

  const startResolved = resolveLatest(startItem);
  const stopResolved = resolveLatest(stopItem);
  const latestTimestamp = [startResolved.timestamp, stopResolved.timestamp]
    .filter((v) => typeof v === "number")
    .sort((a, b) => b - a)[0];

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "number") {
      const abs = Math.abs(value);
      let str = abs >= 1000
        ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : value.toFixed(2);
      str = str.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
      return str;
    }
    return String(value);
  };

  const rowValueSize = Math.max(16, Math.round((basicProps.valueFontSize || 40) * 0.45));
  const startUnit = startItem?.unit || startItem?.raw?.Unit || startItem?.raw?.storageSelection;
  const stopUnit = stopItem?.unit || stopItem?.raw?.Unit || stopItem?.raw?.storageSelection;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        flex: 1,
        width: "100%",
        height: "100%",
        gap: 1,
        px: 1,
      }}
    >
      <Box sx={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, alignItems: "end", width: "min(100%, 460px)" }}>
          <Typography
            sx={{
              fontSize: keyFontSize,
              fontWeight: 400,
              fontFamily: basicProps.fontType || "Roboto",
              color: secondaryText,
              textAlign: "center",
            }}
          >
            Start time
          </Typography>
          <Typography
            sx={{
              fontSize: keyFontSize,
              fontWeight: 400,
              fontFamily: basicProps.fontType || "Roboto",
              color: secondaryText,
              textAlign: "center",
            }}
          >
            Stop time
          </Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 2, alignItems: "baseline", width: "min(100%, 460px)", mx: "auto" }}>
          <Typography
            sx={{
              fontSize: rowValueSize,
              fontWeight: 400,
              fontFamily: basicProps.fontType || "Roboto",
              color: startItem?.color || basicProps.textColor,
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            {formatValue(startResolved.value)}
            {startUnit ? (
              <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400, color: secondaryText }}>
                ({startUnit})
              </span>
            ) : null}
          </Typography>
          <Typography
            sx={{
              fontSize: rowValueSize,
              fontWeight: 400,
              fontFamily: basicProps.fontType || "Roboto",
              color: stopItem?.color || basicProps.textColor,
              lineHeight: 1.2,
              textAlign: "center",
            }}
          >
            {formatValue(stopResolved.value)}
            {stopUnit ? (
              <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 400, color: secondaryText }}>
                ({stopUnit})
              </span>
            ) : null}
          </Typography>
        </Box>
      </Box>
      {/* {isLive && latestTimestamp ? ( //commented for timestamp, has to be removed later
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            fontFamily: basicProps.fontType || "Roboto",
            color: secondaryText,
            textAlign: "center",
            position: "absolute",
            right: 8,
            bottom: 6,
            padding:"10px"
          }}
        >
          {new Date(latestTimestamp).toLocaleString()}
        </Typography>
      ) : null} */}
    </Box>
  );
}

// Reusable line item row
function LineItem({ item, fontType, textColor, themeMode = "light", palette = {} }) {
  const isDarkTheme = themeMode === "dark";
  const lineBg = palette.surface || (isDarkTheme ? "#2C2C2C" : "#FFFFFF");
  const lineBorder = palette.subtleBorder || (isDarkTheme ? "#3B4248" : "#E4E9EC");
  const secondaryText = palette.textSecondary || (isDarkTheme ? "#C7CED3" : "#56636E");
  const primaryText = palette.textPrimary || (isDarkTheme ? "#FFFFFF" : "#1F2A33");
  // Accept value already computed or compute from embedded points
  const rawPoints = item.points || item.raw?.points || [];
  const points = Array.isArray(rawPoints) ? rawPoints : [];
  let displayValue = item.value;
  if (displayValue === undefined || displayValue === null) {
    if (points.length === 1) {
      const v = points[0].y ?? points[0].value ?? points[0].val;
      displayValue = typeof v === "number" && isFinite(v) ? v : null;
    } else if (points.length > 1) {
      let sum = 0;
      let count = 0;
      points.forEach((p) => {
        const v = p.y ?? p.value ?? p.val;
        if (typeof v === "number" && isFinite(v)) {
          sum += v;
          count++;
        }
      });
      if (count) displayValue = sum / count;
    }
  }
  const formatted = (() => {
    if (displayValue === null || displayValue === undefined) return "—";
    if (typeof displayValue === "number") {
      const abs = Math.abs(displayValue);
      let str =
        abs >= 1000
          ? displayValue.toLocaleString(undefined, { maximumFractionDigits: 2 })
          : displayValue.toFixed(2);
      str = str.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
      return str;
    }
    return String(displayValue);
  })();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 1,
        py: 0.5,
        // borderBottom: `1px solid ${lineBorder}`,
        background: item.backgroundColor || lineBg,
        gap: 1,
        minHeight: 30,
        borderBottom: `1px solid ${lineBorder}`,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 400,
            fontFamily: fontType,
            color: item.color || textColor || primaryText,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
          title={item.name}
        >
          {item.name}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 400,
          color: item.color || textColor || primaryText,
          fontFamily: fontType,
          display: "flex",
          alignItems: "baseline",
          gap: 0.5,
        }}
      >
        {formatted} {" "} {item?.raw?.Unit}
        {item.unit ? (
          <span style={{ fontSize: 10, fontWeight: 400, color: secondaryText }}>
            {item.unit} 
          </span>
        ) : null}
      </Typography>
    </Box>
  );
}

const plainIconBtnSx = {
  color: "var(--GreyVariation_2,#5A646C)",
  width: 30,
  height: 30,
  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
};

const STATUS_COLOR_MAP = {
  active: "#22c55e",
  running: "#22c55e",
  ok: "#22c55e",
  normal: "#22c55e",
  online: "#22c55e",
  error: "#ef4444",
  fault: "#ef4444",
  alarm: "#ef4444",
  offline: "#ef4444",
  warning: "#f59e0b",
  degraded: "#f59e0b",
  disabled: "#d1d5db",
  unknown: "#94a3b8",
};

function statusColor(label, fallback = STATUS_COLOR_MAP.disabled) {
  if (!label) return fallback;
  const key = label.toString().trim().toLowerCase();
  return STATUS_COLOR_MAP[key] || fallback;
}

function formatStatusLabel(label, fallback = "Unknown") {
  if (label === null || label === undefined || label === "") return fallback;
  return label.toString();
}

function formatValueForDisplay(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    const abs = Math.abs(value);
    let str =
      abs >= 1000
        ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
        : value.toFixed(2);
    str = str.replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
    return str;
  }
  return value.toString();
}

function formatTimestamp(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}

function formatSourceLabel(source) {
  const normalized = String(source || "").toLowerCase();
  if (normalized === "live") return "Live";
  if (normalized === "historic") return "Historic";
  return "No Data";
}

function StatusCardGrid({ items, textColor, fontType, themeMode = "light", extendedProps = {} }) {
  const isDarkTheme = themeMode === "dark";
  const tableBorder = isDarkTheme ? "#3B4248" : "#E5E9E8";
  const tableHeadBg = isDarkTheme ? "#2A2F35" : "#F3F5F7";
  const tableHeadText = isDarkTheme ? "#C7CED3" : "#5A646C";
  const rowBg = isDarkTheme ? "#2C2C2C" : "#FFFFFF";
  const rowHover = isDarkTheme ? "#343A40" : "#FAFBFC";
  const rowTextPrimary = isDarkTheme ? "#FFFFFF" : "#1F2A33";
  const rowTextSecondary = isDarkTheme ? "#D5DADD" : "#374151";
  const subtleText = isDarkTheme ? "#AEB7BD" : "#6B7280";

  // Build a lookup of per-datapoint colors from extendedProps.datapoints
  const dpColors = React.useMemo(() => {
    const map = {};
    if (Array.isArray(extendedProps?.datapoints)) {
      extendedProps.datapoints.forEach((dp) => {
        const key = dp.tagid || dp.key || dp.id;
        if (key) map[key] = { activeColor: dp.activeColor || "#22c55e", inactiveColor: dp.inactiveColor || "#ef4444" };
      });
    }
    return map;
  }, [extendedProps?.datapoints]);

  const defaultActiveColor = "#22c55e";
  const defaultInactiveColor = "#ef4444";

  const validItems = items && items.length ? items : [{ key: "empty", deviceName: "No devices", status: "Unknown" }];

  return (
    <Box
      sx={{
        // border: "1px solid var(--HighlightedText_Stroke,#E0E5E4)",
        borderRadius: 2,
        p: 1.5,
        // background: "var(--PrimaryBackground,#FFFFFF)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >


      {/* Information Table with Scroll */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: `1px solid ${tableBorder}`,
          borderRadius: 1,
          boxShadow: "none",
          maxHeight: 400,
          // overflow: "scroll",
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ background: tableHeadBg }}>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: tableHeadText,
                  fontFamily: "Roboto",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  borderBottom: `1px solid ${tableBorder}`,
                  py: 0.75,
                  px: 1.5,
                  background: tableHeadBg,
                }}
              >
                Device
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: tableHeadText,
                  fontFamily: "Roboto",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  borderBottom: `1px solid ${tableBorder}`,
                  py: 0.75,
                  px: 1.5,
                  background: tableHeadBg,
                }}
              >
                Tag
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: tableHeadText,
                  fontFamily: "Roboto",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  borderBottom: `1px solid ${tableBorder}`,
                  py: 0.75,
                  px: 1.5,
                  background: tableHeadBg,
                }}
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validItems.map((item, idx) => (
              <TableRow
                key={item.key || idx}
                sx={{
                  background: rowBg,
                  // "&:hover": { background: rowHover },
                  // "&:not(:last-child) td": { borderBottom: `1px solid ${tableBorder}` },
                }}
              >
                <TableCell
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: rowTextPrimary,
                    fontFamily: "Roboto",
                    py: 1,
                    px: 1.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 120,
                  }}
                  title={item?.parentDevice?.title || item?.raw?.parentDevice?.title || item.deviceName}
                >
                  {item?.parentDevice?.title || item?.raw?.parentDevice?.title || item.deviceName || "—"}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: rowTextSecondary,
                    fontFamily: "Roboto",
                    py: 1,
                    px: 1.5,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 140,
                  }}
                  title={item.unit ? `${item.parameter|| "—"} (${item.unit})` : (item.parameter || item.name)}
                >
                  {(() => {
                    const paramLabel = item.parameter || item.name;
                    if (!paramLabel) return "—";
                    const parts = String(paramLabel).split(".");
                    return parts.length > 1 ? parts[parts.length - 1] : String(paramLabel);
                  })()}
                
                  {/* {item.unit ? (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: subtleText,
                        fontFamily: "Roboto",
                        ml: 0.5,
                      }}
                    >
                     ({item.unit}) 
                    </Typography>
                  ) : null} */}
                </TableCell>
               

                <TableCell
                  align="right"
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: rowTextPrimary,
                    fontFamily: "Roboto",
                    py: 1,
                    px: 1.5,
                  }}
                >
                  {/* {formatValueForDisplay(item.value)}
                  {item.unit && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6B7280",
                        fontFamily: "Roboto",
                        ml: 0.5,
                      }}
                    >
                      {item.unit}
                    </Typography>
                  )} */}
                   <RingIndicator color={(() => {
                     const colors = dpColors[item.key] || dpColors[item.raw?.tagid] || {};
                     const active = colors.activeColor || defaultActiveColor;
                     const inactive = colors.inactiveColor || defaultInactiveColor;
                     const val = Number(item.value);
                     if (Number.isNaN(val)) return inactive;
                     return val === 1 ? active : inactive;
                   })()} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  );
}

function StatusCardItem({ item, textColor, fontType }) {
  const deviceStatus = formatStatusLabel(item.status);
  const commStatus = formatStatusLabel(item.communicationStatus);
  const deviceColor = statusColor(item.status);
  const commColor = "#22c55e"; // Green
  const redColor = "#ef4444";  // Red
  return (
    <Box
      sx={{
        // border: "1px solid var(--HighlightedText_Stroke,#E0E5E4)",
        // borderRadius: 2,
        p: 1.25,
        // background: "var(--PrimaryBackground,#FFFFFF)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,

      }}
    >
        {item.timestamp && (
        <Typography sx={{ fontSize: 11, color: "#6B7280", fontFamily: fontType, textAlign: "right" }}>
        {formatTimestamp(item.timestamp)}
        </Typography>
      )}
      {/* Information Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #E5E9E8",
          borderRadius: 1,
          boxShadow: "none",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#F3F5F7" }}>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#5A646C",
                  fontFamily: fontType,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  borderBottom: "1px solid #E5E9E8",
                  py: 0.75,
                  px: 1.5,
                }}
              >
                Device
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#5A646C",
                  fontFamily: fontType,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  borderBottom: "1px solid #E5E9E8",
                  py: 0.75,
                  px: 1.5,
                }}
              >
                Tag
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#5A646C",
                  fontFamily: fontType,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  borderBottom: "1px solid #E5E9E8",
                  py: 0.75,
                  px: 1.5,
                }}
              >
                Value
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ background: "#FFFFFF", "&:hover": { background: "#FAFBFC" } }}>
              <TableCell
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1F2A33",
                  fontFamily: fontType,
                  borderBottom: "none",
                  py: 1,
                  px: 1.5,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 100,
                }}
                title={item?.parentDevice?.title || item?.raw?.parentDevice?.title}
              >
                {item?.parentDevice?.title || item?.raw?.parentDevice?.title || "—"}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#374151",
                  fontFamily: fontType,
                  borderBottom: "none",
                  py: 1,
                  px: 1.5,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 120,
                }}
                title={item.parameter}
              >
                {item.parameter || "—"}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#111827",
                  fontFamily: fontType,
                  borderBottom: "none",
                  py: 1,
                  px: 1.5,
                  
                }}
              >
                {/* {formatValueForDisplay(item.value)} */}
                {item.unit && (
                  // <Typography
                  //   component="span"
                  //   sx={{
                  //     fontSize: 11,
                  //     fontWeight: 600,
                  //     color: "#6B7280",
                  //     fontFamily: fontType,
                  //     ml: 0.5,
                  //   }}
                  // >
                     <RingIndicator color={commColor} />
                  // </Typography>
                )}
                
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

    
    </Box>
  );
}

function RingIndicator({ color, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.6)",
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "#FFFFFF",
          }}
        />
      </Box>
      { label && 
      (
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#4B5563" }}>
        {label}
      </Typography>
      )

      }
    </Box>
  );
}

function StatusBadge({ label, color, fontType }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: 999,
        backgroundColor: `${color}1A`,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: color,
        }}
      />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: fontType }}>
        {label}
      </Typography>
    </Box>
  );
}

function StatusLegend({ fontType }) {
  const legendItems = [
    { label: "Active / Online", color: STATUS_COLOR_MAP.active },
    { label: "Error / Offline", color: STATUS_COLOR_MAP.error },
    { label: "Disabled", color: STATUS_COLOR_MAP.disabled },
  ];
  return (
    <Box
      sx={{
        border: "1px solid var(--HighlightedText_Stroke,#E0E5E4)",
        borderRadius: 2,
        p: 1,
        background: "var(--PrimaryBackground,#FFFFFF)",
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      {legendItems.map((item) => (
        <StatusBadge key={item.label} label={item.label} color={item.color} fontType={fontType} />
      ))}
    </Box>
  );
}
